import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useLogin, checkLoggedIn, getUsername } from "./authConfig";
import { LoginContext } from "./loginContext";
import Layout from "./pages/layout/Layout";
import Login from "./pages/login/Login";
import { TermsAndConditions } from "./components/TermsAndConditions/TermsAndConditions";

// Bump this version string whenever the T&C text changes to force all users to re-accept.
const TERMS_VERSION = "v1";

const getTermsKey = (username: string) => `terms_accepted_${TERMS_VERSION}_${username}`;

const LayoutWrapper = () => {
    const [loggedIn, setLoggedIn] = useState(false);
    const [checkingLogin, setCheckingLogin] = useState(true);
    const [termsAccepted, setTermsAccepted] = useState(false);

    if (useLogin) {
        const { instance } = useMsal();
        // Keep track of the mounted state to avoid setting state in an unmounted component
        const mounted = useRef<boolean>(true);
        useEffect(() => {
            mounted.current = true;
            checkLoggedIn(instance)
                .then(async isLoggedIn => {
                    if (!mounted.current) return;
                    setLoggedIn(isLoggedIn);
                    setCheckingLogin(false);
                    if (isLoggedIn) {
                        const username = (await getUsername(instance)) ?? "anonymous";
                        if (mounted.current) {
                            setTermsAccepted(localStorage.getItem(getTermsKey(username)) === "true");
                        }
                    }
                })
                .catch(e => {
                    console.error("checkLoggedIn failed", e);
                    if (mounted.current) {
                        setCheckingLogin(false);
                    }
                });
            return () => {
                mounted.current = false;
            };
        }, [instance]);

        const handleAcceptTerms = async () => {
            const username = (await getUsername(instance)) ?? "anonymous";
            localStorage.setItem(getTermsKey(username), "true");
            setTermsAccepted(true);
        };

        // Show a loading spinner while checking login status.
        // IMPORTANT: Do NOT render <Login /> here — it would trigger loginRedirect()
        // before the check completes, causing a redirect loop with Microsoft Entra ID.
        if (checkingLogin) {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                    <Spinner size={SpinnerSize.large} label="Checking authentication..." />
                </div>
            );
        }

        return (
            <LoginContext.Provider value={{ loggedIn, setLoggedIn }}>
                {loggedIn ? (
                    termsAccepted ? (
                        <Layout />
                    ) : (
                        <TermsAndConditions onAccept={handleAcceptTerms} />
                    )
                ) : (
                    <Login />
                )}
            </LoginContext.Provider>
        );
    } else {
        return (
            <LoginContext.Provider
                value={{
                    loggedIn,
                    setLoggedIn
                }}
            >
                <Layout />
            </LoginContext.Provider>
        );
    }
};

export default LayoutWrapper;
